import React, { useState, useCallback } from 'react';
import { Heading, Text, Button, Flex } from '@vibe/core';
import { useReportStore } from '../../stores/reportStore';
import type { WorklogReportResponse } from '../../api/report.api';
import { createBookmark } from '../../api/report.api';
import WorklogReportExportButton from './WorklogReportExportButton';
import BookmarkDialog from './BookmarkDialog';
import { formatHours } from '../../utils/timesheet-capacity';

interface Props {
  data: WorklogReportResponse;
}

const WorklogReportHeader: React.FC<Props> = ({ data }) => {
  const { currentConfig, setView, setBookmarks, bookmarks } = useReportStore();
  const [showBookmarkDialog, setShowBookmarkDialog] = useState(false);
  const [bookmarking, setBookmarking] = useState(false);

  const handleBookmark = useCallback(
    async (name: string) => {
      if (!currentConfig) return;
      setBookmarking(true);
      try {
        const bookmark = await createBookmark({
          name,
          reportType: 'detailed_worklog',
          config: currentConfig,
        });
        setBookmarks([...bookmarks, bookmark]);
        setShowBookmarkDialog(false);
      } catch (err) {
        console.error('Failed to bookmark report:', err);
      } finally {
        setBookmarking(false);
      }
    },
    [currentConfig, bookmarks, setBookmarks],
  );

  const formatPeriodDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="rp-report-header">
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
      >
        <div>
          <Heading type={Heading.types.H2}>Detailed Worklog Report</Heading>
          <Text type={Text.types.TEXT2} style={{ color: '#676879', marginTop: 4 }}>
            Period: {formatPeriodDate(data.period.startDate)} &ndash;{' '}
            {formatPeriodDate(data.period.endDate)}
          </Text>
          <Flex gap={Flex.gaps.MEDIUM} style={{ marginTop: 4 }}>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalUsers} users
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalWorklogs} worklogs
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
              {data.summary.totalHours === 0 ? '0' : formatHours(data.summary.totalHours)}h total
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#00854d' }}>
              {data.summary.totalBillableHours}h billable
            </Text>
            <Text type={Text.types.TEXT2} style={{ color: '#fdab3d' }}>
              {data.summary.totalNonBillableHours}h non-billable
            </Text>
          </Flex>
        </div>
        <Flex gap={Flex.gaps.SMALL}>
          <WorklogReportExportButton data={data} />
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setShowBookmarkDialog(true)}
          >
            Bookmark
          </Button>
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('config')}
          >
            Customize Report
          </Button>
          <Button
            kind={Button.kinds.TERTIARY}
            size={Button.sizes.SMALL}
            onClick={() => setView('gallery')}
          >
            Back to Reports
          </Button>
        </Flex>
      </Flex>

      {showBookmarkDialog && (
        <BookmarkDialog
          saving={bookmarking}
          onSave={handleBookmark}
          onClose={() => setShowBookmarkDialog(false)}
        />
      )}
    </div>
  );
};

export default WorklogReportHeader;
